import { useMemo } from "react";
import {
  AtSignIcon,
  CopyIcon,
  ForwardIcon,
  LandmarkIcon,
  MailIcon,
} from "lucide-react";
import { StripeIcon } from "@/objects/@icons";
import { Link as RouterLink } from "react-router-dom";

import {
  FinancialAccountSyncStateEnum,
  type EntityFragment,
  type EntityIntegrationsQuery,
} from "@/__generated__/graphql";
import { COMMAND_ID as ADD_BANK_ACCOUNT_COMMAND_ID } from "@/app/app/_/commands/AddBankAccountCommand";
import { openConnectStripeFlow } from "@/app/app/_/commands/utils";
import { SettingsViewCard } from "@/app/app/_/views/SettingsView/SettingsViewLayout";
import { BoxForOrganization } from "@/components/atoms/BoxForOrganization";
import { Icon } from "@/components/atoms/Icon";
import { SimpleEnum } from "@/components/molecules/SimpleEnum";
import { Queries } from "@/constants";
import { EnumColors } from "@/constants/values";
import { useBackendQuery } from "@/hooks/useBackendQuery";
import { State } from "@/state";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import { Typography } from "@/components/ui/typography";
import { cn } from "@/components/ui/utils";

type Institution = NonNullable<
  EntityIntegrationsQuery["financialInstitutions"]["data"]
>[number];

/** The bank drawn while the banks load: a typical one, each piece wrapped. */
const PLACEHOLDER_INSTITUTION = {
  __typename: "FinancialInstitutionObject",
  id: "placeholder",
  institutionName: "Bank of Somewhere",
  financialAccounts: [],
} as unknown as Institution;

type EntityIntegrationsPanelProps = {
  entity: EntityFragment;
};

export function EntityIntegrationsPanel({
  entity,
}: EntityIntegrationsPanelProps) {
  // Bank feeds are this tab's payload — not on the base Entity fragment.
  const { data, loading } = useBackendQuery(Queries.ENTITY_INTEGRATIONS_QUERY, {
    variables: { entityIdentifiers: [entity.identifier] },
    fetchPolicy: "cache-and-network",
  });
  const institutions: Institution[] = useMemo(
    () => data?.financialInstitutions?.data ?? [],
    [data],
  );
  const isLoadingBanks = loading && institutions.length === 0;

  const isStripeConnected = Boolean(entity.integrations?.stripe?.isActive);
  const address = entity.inboundForwardingAddress;

  return (
    <div className="flex flex-col gap-3 p-4">
      {/* A mailbox is its owner's, connected once and given to any of their
          businesses - managed in their own settings, not a business's. */}
      <SettingsViewCard
        icon={MailIcon}
        title="Mailboxes"
        description={`Give your Gmail or Outlook mailboxes to ${entity.name} in Settings › Emails.`}
        actions={
          <Button variant="outline" size="sm" asChild>
            <RouterLink to="/app/settings/emails">Manage</RouterLink>
          </Button>
        }
      />

      <SettingsViewCard
        icon={ForwardIcon}
        title="Forward bills and receipts"
        description="A member forwards an email with its attachments here, from their Neoledger address."
      >
        <div className="flex items-center gap-3 pl-3 pr-3 min-h-11 [border-top-width:1px] [border-top-style:solid] border-border">
          <div className="flex text-muted-foreground/80">
            <Icon ReactIcon={AtSignIcon} size="md" />
          </div>
          <Typography
            level="body-md"
            className={cn(
              "flex-1 min-w-0",
              address && "font-mono",
              address ? "text-foreground" : "text-muted-foreground/80",
              "break-all",
            )}
          >
            {address ?? "Not set up for this environment yet."}
          </Typography>
          {address && (
            <Button
              variant="ghost"
              size="icon-xs"
              onClick={() => {
                void navigator.clipboard.writeText(address);
                State.getState().setSnackbarMessage(
                  "Copied forwarding address",
                );
              }}
              aria-label="Copy forwarding address"
              data-testid="copy-inbound-forwarding-address"
            >
              <Icon ReactIcon={CopyIcon} size="sm" />
            </Button>
          )}
        </div>
      </SettingsViewCard>

      <SettingsViewCard
        icon={LandmarkIcon}
        title="Bank feeds"
        description={
          !isLoadingBanks && institutions.length === 0
            ? "No bank connected yet. A connected bank sends its transactions in on its own."
            : "Connected banks send their transactions in on their own."
        }
        actions={
          <Button
            variant="outline"
            size="sm"
            onClick={() =>
              State.getState().pushToCommandStack({
                commandDefinitionId: ADD_BANK_ACCOUNT_COMMAND_ID,
              })
            }
          >
            Connect a bank
          </Button>
        }
        data-testid="entity-integrations-banks"
      >
        {(isLoadingBanks ? [PLACEHOLDER_INSTITUTION] : institutions).map(
          (institution) => {
            const states = institution.financialAccounts.map((account) =>
              account.__typename === "FinancialAccountObject"
                ? account.syncState
                : null,
            );
            return (
              <div
                key={institution.id}
                className="flex items-center gap-3 pl-3 pr-3 min-h-11 [border-top-width:1px] [border-top-style:solid] border-border"
              >
                <Skeleton
                  loading={isLoadingBanks}
                  variant="rectangular"
                  className="w-fit max-w-full flex [flex-direction:inherit] [flex-wrap:inherit] [align-items:inherit] [justify-content:inherit] gap-[inherit]"
                >
                  {/* Its logo where the bank is known as a company. */}
                  {institution.organization ? (
                    <BoxForOrganization
                      organization={institution.organization}
                    />
                  ) : (
                    <div className="flex text-muted-foreground/80">
                      <Icon ReactIcon={LandmarkIcon} size="md" />
                    </div>
                  )}
                </Skeleton>
                <div className="flex-1 min-w-0 pt-1.5 pb-1.5">
                  <Typography level="body-md" className="truncate">
                    <Skeleton loading={isLoadingBanks} variant="inline">
                      {institution.institutionName ?? "Bank"}
                    </Skeleton>
                  </Typography>
                  {institution.financialAccounts.length > 0 && (
                    <Typography
                      level="body-sm"
                      className="truncate text-muted-foreground/80"
                    >
                      {institution.financialAccounts
                        .map((account) =>
                          account.__typename === "FinancialAccountObject"
                            ? `${account.name}${account.mask ? ` ••${account.mask}` : ""}`
                            : null,
                        )
                        .filter(Boolean)
                        .join(", ")}
                    </Typography>
                  )}
                </div>
                <Skeleton
                  loading={isLoadingBanks}
                  variant="rectangular"
                  className="w-fit max-w-full flex [flex-direction:inherit] [flex-wrap:inherit] [align-items:inherit] [justify-content:inherit] gap-[inherit]"
                >
                  {states.includes(
                    FinancialAccountSyncStateEnum.ReconnectRequired,
                  ) ? (
                    <SimpleEnum
                      label="Reconnect needed"
                      enumColor={EnumColors.ORANGE}
                    />
                  ) : states.includes(FinancialAccountSyncStateEnum.Failed) ? (
                    <SimpleEnum
                      label="Sync failed"
                      enumColor={EnumColors.RED}
                    />
                  ) : states.includes(FinancialAccountSyncStateEnum.Syncing) ? (
                    <SimpleEnum label="Syncing" enumColor={EnumColors.GREY_1} />
                  ) : (
                    <SimpleEnum
                      label="Connected"
                      enumColor={EnumColors.GREEN}
                    />
                  )}
                </Skeleton>
              </div>
            );
          },
        )}
      </SettingsViewCard>

      <SettingsViewCard
        icon={StripeIcon}
        title="Stripe"
        description="Marks your Neoledger invoices paid when customers pay online."
        actions={
          isStripeConnected ? (
            <SimpleEnum label="Connected" enumColor={EnumColors.GREEN} />
          ) : (
            <Button variant="outline" size="sm" onClick={openConnectStripeFlow}>
              Connect Stripe
            </Button>
          )
        }
      />
    </div>
  );
}
